import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Intent } from '@blueprintjs/core';
import { Button, ButtonSave } from 'components/buttons';
import { DataDisplayTable } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { FormDateInput, FormHelperText, FormSwitch, FormTextArea, FormTextInput } from 'components/form-fields';
import { GridLayout } from 'components/grid';
import { ShowHide } from 'components/layout';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { HelperFunctions } from 'helpers';
import { useValidation } from 'hooks/useValidation';
import { showSaveFailureToast } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { EmployeeService } from 'services';

const emptyFieldValue = "N/A";
const dateApiFormat = "YYYY-MM-DD";
const loadFailedInlineMessage = "The employee's profile information is currently unavailable. Refresh the page to try again.";
const refreshFailedInlineMessage = "The employee's profile information could not be checked for changes, so the values shown may be out of date. Refresh the page before editing them.";

// The backing columns are plain SQL DATE values with no range validation, and an
// employee's licence expiry can legitimately be long past. FormDateInput otherwise
// defaults to a narrow 30-months-back window and reports such dates as out of range.
const earliestSelectableDate = moment("1900-01-01").toDate();
const latestSelectableDate = moment("2100-01-01").toDate();

// The read-view field set and labels match the backend's ProfileInformationSnapshotBuilder
// (Employee.Services/EmployeeReview/ProfileInformationSnapshotBuilder.cs) so the wizard and
// the completed-review record present the same profile fields identically.
function buildProfileFieldRows(employee) {
    const location = employee?.employeeLocation ?? {};

    return [
        { key: "reference-number", header: "Reference Number", value: location.reference || emptyFieldValue },
        { key: "email-address", header: "Email Address", value: location.email || emptyFieldValue },
        {
            key: "driver-licence-expiry",
            header: "Driver Licence Expiry",
            value: <DateToLocal format="DD/MM/YYYY" invalidDateString={emptyFieldValue}>{employee?.employeeLicenceRenewalDate}</DateToLocal>
        },
        {
            key: "lgv-licence-expiry",
            header: "LGV Licence Expiry",
            value: <DateToLocal format="DD/MM/YYYY" invalidDateString={emptyFieldValue}>{employee?.hgvLicenceRenewalDate}</DateToLocal>
        },
        {
            key: "adr-expiry-date",
            header: "ADR Expiry Date",
            value: <DateToLocal format="DD/MM/YYYY" invalidDateString={emptyFieldValue}>{employee?.adrExpiryDate}</DateToLocal>
        },
        {
            key: "psv-expiry-date",
            header: "PSV Expiry Date",
            value: <DateToLocal format="DD/MM/YYYY" invalidDateString={emptyFieldValue}>{employee?.psvExpiryDate}</DateToLocal>
        },
        { key: "works-for-another-company", header: "Works for another company", value: location.worksForAnotherCompany ? "Yes" : "No" }
    ];
}

// The Profile Information step of the complete-review wizard. Read mode shows the
// employee's live profile fields; Edit mode lets the manager correct them via the
// employee record's existing read/update endpoints (no new write path - see PBI 167757).
export function ProfileInformationStep(
    {
        state = null,
        employeeId = null,
        loading = false,
        onChange
    }
) {

    const [valid, errors, validate] = useValidation();

    const employee = state?.record ?? null;
    const draft = state?.draft ?? null;
    const editing = state?.editing === true;
    const saving = state?.saving === true;
    const loadFailed = state?.loadFailed === true;
    // The save sends the whole loaded record, so a record that could not be re-read is presented
    // as it stands but cannot be edited - saving it could revert a change made since it was read.
    const refreshFailed = state?.refreshFailed === true;

    // Mirror the backend's email-format rule on the editable email so an invalid address is
    // caught before the save. Email is optional on the employee profile (an empty value is
    // allowed and accepted by the backend), and useValidation's "email" type has no
    // empty-value guard, so the rule is only applied when a value is present. A masked
    // address (e.g. "t******e@example.com") satisfies the regex and passes.
    useEffect(() => {
        if (!editing) {
            return;
        }

        const email = draft?.employeeLocation?.email;
        const rules = email ? [{ fieldName: "email", type: "email" }] : [];

        validate(rules, { email });
    }, [editing, draft]);

    function onDraftFieldChange(fieldName, value) {
        onChange({
            draft: {
                ...draft,
                [fieldName]: value
            }
        });
    }

    function onDraftLocationFieldChange(fieldName, value) {
        onChange({
            draft: {
                ...draft,
                employeeLocation: {
                    ...draft.employeeLocation,
                    [fieldName]: value
                }
            }
        });
    }

    function onDraftDateFieldChange(fieldName, item) {
        onDraftFieldChange(fieldName, item ? moment(item).format(dateApiFormat) : null);
    }

    function onEdit() {
        onChange({ draft: HelperFunctions.deepClone(employee), editing: true });
    }

    function onCancel() {
        onChange({ draft: HelperFunctions.deepClone(employee), editing: false });
    }

    function onSave() {
        // Block the save while the email format is invalid; the inline error is already
        // shown against the field by the validation effect above.
        if (!valid) {
            return;
        }

        onChange({ saving: true });

        // GET /employee/Employees/{id} rewrites employeeLocation.profilePicture into a
        // signed blob (SAS) URL; posting that signed URL back in the PUT body trips the
        // Azure WAF and returns a 403 (bug 171239). The picture has its own dedicated
        // endpoint and is not a parameter on the employee update proc, so remove it from
        // the payload entirely - a clone is used so the draft state is not mutated.
        const payload = HelperFunctions.deepClone(draft);
        if (payload.employeeLocation != null) {
            delete payload.employeeLocation.profilePicture;
        }

        EmployeeService.updateEmployee(employeeId, payload).then(function () {
            onChange({ record: HelperFunctions.deepClone(draft), editing: false, saving: false });
            NotificationToaster.show(Intent.SUCCESS, "Employee profile has been successfully updated.");
        }, function (error) {
            onChange({ saving: false });
            showSaveFailureToast(error, "Failed to update the employee profile. Please try again.");
        });
    }

    function onCommentChange(event) {
        onChange({ comment: event.target.value });
    }

    return (
        <div>
            <div className="inline-items inline-items-space spacer-bottom-small">
                <h2>Profile Information</h2>
                <div className="button-row">
                    <ShowHide
                        evaluator={!editing}
                        show={(
                            <Button id="profile-information-edit" icon="edit" text="Edit" loading={loading} disabled={saving || loading || loadFailed || refreshFailed} onClick={onEdit} />
                        )}
                        hide={(
                            <Fragment>
                                <Button id="profile-information-cancel" text="Cancel" loading={loading} disabled={saving} onClick={onCancel} />
                                <ButtonSave id="profile-information-save" loading={saving} disabled={saving} simpleDisabled={!valid || loading} onClick={onSave} />
                            </Fragment>
                        )}
                    />
                </div>
            </div>
            <ShowHide
                evaluator={loadFailed}
                show={(
                    <NotificationInline
                        allowClose={false}
                        show
                        text={loadFailedInlineMessage}
                        intent="danger"
                    />
                )}
                hide={(
                    <Fragment>
                        <ShowHide
                            evaluator={refreshFailed}
                            show={(
                                <NotificationInline
                                    allowClose={false}
                                    show
                                    text={refreshFailedInlineMessage}
                                    intent="warning"
                                />
                            )}
                        />
                        <FormHelperText loading={loading}>
                            {editing
                                ? "Update the employee's profile details."
                                : "Check the employee's profile details are correct and up to date."}
                        </FormHelperText>
                        <ShowHide
                            evaluator={!editing}
                            show={(
                                <DataDisplayTable data={buildProfileFieldRows(employee)} loading={loading} />
                            )}
                            hide={(
                                <GridLayout numberOfColumns={2} gap={[0, 20]}>
                                    <FormTextInput
                                        id="profile-reference-number"
                                        headingText="Reference Number"
                                        value={draft?.employeeLocation?.reference ?? ""}
                                        loading={loading}
                                        disabled={saving}
                                        onChange={(event) => onDraftLocationFieldChange("reference", event.target.value)}
                                    />
                                    <FormTextInput
                                        id="profile-email-address"
                                        headingText="Email Address"
                                        value={draft?.employeeLocation?.email ?? ""}
                                        loading={loading}
                                        disabled={saving}
                                        dangerHelperText={errors.email}
                                        onChange={(event) => onDraftLocationFieldChange("email", event.target.value)}
                                    />
                                    <FormDateInput
                                        id="profile-driver-licence-expiry"
                                        headingText="Driver Licence Expiry"
                                        value={draft?.employeeLicenceRenewalDate}
                                        minDate={earliestSelectableDate}
                                        maxDate={latestSelectableDate}
                                        loading={loading}
                                        disabled={saving}
                                        onChange={(item) => onDraftDateFieldChange("employeeLicenceRenewalDate", item)}
                                    />
                                    <FormDateInput
                                        id="profile-lgv-licence-expiry"
                                        headingText="LGV Licence Expiry"
                                        value={draft?.hgvLicenceRenewalDate}
                                        minDate={earliestSelectableDate}
                                        maxDate={latestSelectableDate}
                                        loading={loading}
                                        disabled={saving}
                                        onChange={(item) => onDraftDateFieldChange("hgvLicenceRenewalDate", item)}
                                    />
                                    <FormDateInput
                                        id="profile-adr-expiry-date"
                                        headingText="ADR Expiry Date"
                                        value={draft?.adrExpiryDate}
                                        minDate={earliestSelectableDate}
                                        maxDate={latestSelectableDate}
                                        loading={loading}
                                        disabled={saving}
                                        onChange={(item) => onDraftDateFieldChange("adrExpiryDate", item)}
                                    />
                                    <FormDateInput
                                        id="profile-psv-expiry-date"
                                        headingText="PSV Expiry Date"
                                        value={draft?.psvExpiryDate}
                                        minDate={earliestSelectableDate}
                                        maxDate={latestSelectableDate}
                                        loading={loading}
                                        disabled={saving}
                                        onChange={(item) => onDraftDateFieldChange("psvExpiryDate", item)}
                                    />
                                    <FormSwitch
                                        id="profile-works-for-another-company"
                                        headingText="Works for another company"
                                        checked={draft?.employeeLocation?.worksForAnotherCompany ?? false}
                                        loading={loading}
                                        disabled={saving}
                                        onChange={(event) => onDraftLocationFieldChange("worksForAnotherCompany", event.target.checked)}
                                    />
                                </GridLayout>
                            )}
                        />
                    </Fragment>
                )}
            />
            <div className="spacer-top">
                <FormTextArea
                    id="profile-information-comment"
                    headingText="Overall comments"
                    placeholder="Add any overall comments for this topic..."
                    value={state?.comment ?? ""}
                    loading={loading}
                    disabled={saving}
                    fill
                    onChange={onCommentChange}
                />
            </div>
        </div>
    );
}

ProfileInformationStep.propTypes = {
    state: PropTypes.object,
    employeeId: PropTypes.number,
    loading: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};
